import { CSSObject, Theme } from '@mui/material';
import { PortalContainerStyle } from 'types/theme';

/**
 * Named styled-container presets for the admin-portal layout shell.
 *
 * Each region reads its style from `theme.portalStyles` (populated from the
 * `--descope-admin-portal-*` vars in the fetched style), falling back to the
 * MUI palette defaults when a region is not defined in the style. A border or
 * background image is applied ONLY when the region itself defines one — never a
 * default. A background image, when set, sits on top of the background color.
 */
const bgImage = (override: PortalContainerStyle | undefined): CSSObject =>
	override?.backgroundImage
		? {
				backgroundImage: override.backgroundImage,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundRepeat: 'no-repeat'
			}
		: {};

const panel = (
	override: PortalContainerStyle | undefined,
	fallback: CSSObject
): CSSObject => ({
	backgroundColor: override?.backgroundColor ?? fallback.backgroundColor,
	...(override?.border ? { border: override.border } : {}),
	...bgImage(override)
});

export const layoutContainerSx = {
	adminPortalAppBackground: (theme: Theme): CSSObject => ({
		backgroundColor:
			theme.portalStyles?.background?.backgroundColor ??
			theme.palette.grey.highlight,
		...bgImage(theme.portalStyles?.background)
	}),
	adminPortalHeader: (theme: Theme): CSSObject => {
		const override = theme.portalStyles?.header;
		return {
			backgroundColor: override?.backgroundColor ?? theme.palette.grey.main,
			// the header carries its border on the bottom edge only
			...(override?.border ? { borderBottom: override.border } : {}),
			...bgImage(override)
		};
	},
	adminPortalSidebarPanel: (theme: Theme): CSSObject =>
		panel(theme.portalStyles?.sidebar, {
			backgroundColor: theme.palette.grey.main
		}),
	adminPortalContentPanel: (theme: Theme): CSSObject =>
		panel(theme.portalStyles?.content, {
			backgroundColor: theme.palette.grey.main
		})
};
